import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useProjetStore } from '../stores/useProjetStore';
import { useClientStore } from '../stores/useClientStore';
import { StatutProjet } from '../types';
import { extractErrorMessage } from '../utils/errorUtils';
import AddressAutocomplete from './AddressAutocomplete';

interface ProjetSelectorProps {
  clientId?: string;
  value: number | null;
  onChange: (projetId: number | null) => void;
  error?: boolean;
  helperText?: string;
}

const ProjetSelector: React.FC<ProjetSelectorProps> = ({
  clientId,
  value,
  onChange,
  error,
  helperText,
}) => {
  const { projets, loadProjets, loadProjetsByClientId, addProjet } = useProjetStore();
  const { clients } = useClientStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [newProjet, setNewProjet] = useState({
    titre: '',
    description: '',
    adresseChantier: '',
    codePostal: '',
    ville: '',
    clientId: '',
  });
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Charger les projets au montage
  useEffect(() => {
    if (clientId) {
      loadProjetsByClientId(Number(clientId));
    } else {
      loadProjets();
    }
  }, [clientId, loadProjets, loadProjetsByClientId]);

  // Filtrer les projets par client si nécessaire
  const filteredProjets = clientId
    ? projets.filter((p) => p.clientId === Number(clientId))
    : projets;

  const handleOpenDialog = () => {
    setNewProjet({
      titre: '',
      description: '',
      adresseChantier: '',
      codePostal: '',
      ville: '',
      clientId: clientId || '',
    });
    setDialogError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogError(null);
  };

  const handleCreateProjet = async () => {
    if (!newProjet.titre.trim()) {
      setDialogError('Le titre du projet est obligatoire');
      return;
    }
    if (!newProjet.adresseChantier.trim()) {
      setDialogError("L'adresse du chantier est obligatoire");
      return;
    }
    if (!newProjet.codePostal.trim()) {
      setDialogError("Le code postal est obligatoire");
      return;
    }
    if (!newProjet.ville.trim()) {
      setDialogError("La ville est obligatoire");
      return;
    }
    if (!newProjet.clientId) {
      setDialogError('Veuillez sélectionner un client');
      return;
    }

    try {
      // Concaténer l'adresse complète : adresse + code postal + ville
      const adresseComplete = `${newProjet.adresseChantier}, ${newProjet.codePostal} ${newProjet.ville}`;

      const createdProjet = await addProjet({
        titre: newProjet.titre,
        description: newProjet.description,
        adresseChantier: adresseComplete,
        clientId: Number(newProjet.clientId),
        statut: StatutProjet.EN_ATTENTE_DEVIS,
      });

      onChange(createdProjet.id);
      handleCloseDialog();
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du projet');
      setDialogError(errorMessage);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <FormControl fullWidth error={error}>
          <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Projet</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            label="Projet"
            sx={{ '& .MuiSelect-select': { py: 2 } }}
          >
            <MenuItem value="">
              <em>Sélectionnez un projet</em>
            </MenuItem>
            {filteredProjets.map((projet) => (
              <MenuItem key={projet.id} value={projet.id}>
                {projet.titre} - {projet.adresseChantier}
              </MenuItem>
            ))}
          </Select>
          {helperText && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
              {helperText}
            </Typography>
          )}
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<AddCircleOutline />}
          onClick={handleOpenDialog}
          sx={{
            minWidth: '150px',
            height: '56px',
            whiteSpace: 'nowrap',
          }}
        >
          Nouveau projet
        </Button>
      </Box>

      {/* Dialog pour créer un nouveau projet */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un nouveau projet</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Titre du projet *"
            value={newProjet.titre}
            onChange={(e) => setNewProjet({ ...newProjet, titre: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={newProjet.description}
            onChange={(e) => setNewProjet({ ...newProjet, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <AddressAutocomplete
            label="Adresse du chantier *"
            value={newProjet.adresseChantier}
            onChange={(value) => setNewProjet({ ...newProjet, adresseChantier: value })}
            onAddressSelect={(address) => {
              // Remplir automatiquement les champs ville et code postal
              setNewProjet({
                ...newProjet,
                adresseChantier: address.adresse,
                codePostal: address.codePostal,
                ville: address.ville,
              });
            }}
            placeholder="Ex: 8 Boulevard du Port"
            required
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Ville *"
              value={newProjet.ville}
              onChange={(e) => setNewProjet({ ...newProjet, ville: e.target.value })}
            />

            <TextField
              label="Code postal *"
              value={newProjet.codePostal}
              onChange={(e) => setNewProjet({ ...newProjet, codePostal: e.target.value })}
              sx={{ width: '200px' }}
            />
          </Box>

          {!clientId && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Client *</InputLabel>
              <Select
                value={newProjet.clientId}
                onChange={(e) => setNewProjet({ ...newProjet, clientId: e.target.value })}
                label="Client *"
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.prenom} {client.nom} - {client.ville}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleCreateProjet} variant="contained" color="primary">
            Créer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjetSelector;
